import { useCallback, useEffect, useState } from "react";
import {
  Bell,
  ChevronRight,
  Heart,
  HelpCircle,
  HousePlus,
  LogOut,
  MapPin,
  Package,
  Plus,
  Trash2,
  WalletCards,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError, formatINR } from "../lib/api";
import { MapPicker } from "../components/MapPicker";

const initialAddress = { label: "Home", address: "", phone: "", latitude: null, longitude: null };

function AccountLink({ icon: Icon, label, detail, to, testId }) {
  return (
    <Link
      to={to}
      className="flex min-h-16 items-center gap-4 border-b border-stone-100 px-1 py-3 transition-colors hover:text-orange-700"
      data-testid={testId}
    >
      <span className="grid h-10 w-10 place-items-center bg-stone-100 text-stone-900">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-stone-950">{label}</span>
        {detail && <span className="mt-0.5 block text-xs text-stone-500">{detail}</span>}
      </span>
      <ChevronRight className="h-4 w-4 text-stone-400" />
    </Link>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [address, setAddress] = useState(initialAddress);
  const [showForm, setShowForm] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [savingAddress, setSavingAddress] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    api.get("/account/wallet")
      .then((response) => setWalletBalance(response.data.balance || 0))
      .catch(() => setWalletBalance(0));
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/account/notifications");
      setNotifications(data.items || []);
      if ((data.unread || 0) > 0) {
        await api.post("/account/notifications/read-all");
      }
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const loadAddresses = useCallback(async () => {
    setLoadingAddresses(true);
    try {
      const { data } = await api.get("/account/addresses");
      setAddresses(data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoadingAddresses(false);
    }
  }, []);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  useEffect(() => {
    api.get("/business-info")
      .then((response) => setBusinessInfo(response.data))
      .catch(() => setBusinessInfo({}));
  }, []);

  const saveAddress = async (event) => {
    event.preventDefault();
    setSavingAddress(true);
    try {
      await api.post("/account/addresses", address);
      setAddress(initialAddress);
      setShowForm(false);
      toast.success("Address saved");
      loadAddresses();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingAddress(false);
    }
  };

  const removeAddress = async (addressId) => {
    try {
      await api.delete(`/account/addresses/${addressId}`);
      setAddresses((current) => current.filter((item) => item.id !== addressId));
      toast.success("Address removed");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("You are signed out");
  };

  return (
    <main className="mx-auto min-h-[70vh] max-w-4xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <section className="-mx-4 bg-gradient-to-br from-[#FF5200] to-[#ff2d55] px-4 pb-8 pt-7 text-white md:-mx-6 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/80">My Account</p>
        <div className="mt-4 flex items-center gap-4">
        {user?.picture ? (
          <img
            src={user.picture}
            alt=""
            className="h-16 w-16 rounded-full border-2 border-white/80 object-cover"
            data-testid="account-avatar"
          />
        ) : (
          <span
            className="grid h-16 w-16 place-items-center rounded-full bg-white text-lg font-semibold text-[#FF5200]"
            data-testid="account-avatar-fallback"
          >
            {(user?.name || "Q").slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-lg font-bold" data-testid="account-name">
            {user?.name}
          </p>
          <p className="truncate text-sm text-white/80" data-testid="account-email">
            {user?.email}
          </p>
          {user?.phone && (
            <p className="mt-1 text-xs text-white/80" data-testid="account-phone">
              {user.phone}
            </p>
          )}
          <p className="mt-1 text-xs text-white/70" data-testid="account-join-date">
            Member since {new Date(user?.created_at || Date.now()).toLocaleDateString()}
          </p>
        </div>
        </div>
      </section>

      <section className="mt-4" data-testid="account-quick-cards">
        <Link to="/wallet" className="flex items-center gap-3 rounded-xl bg-slate-950 p-4 text-white" data-testid="account-wallet-card"><WalletCards size={22} /><span className="flex-1"><b className="block text-sm">My Wallet</b><span className="text-xs text-white/60">Balance</span></span><b className="rounded-full bg-white/15 px-3 py-1 text-sm" data-testid="account-wallet-balance">{formatINR(walletBalance)}</b><ChevronRight size={17} /></Link>
      </section>

      {notifications.length > 0 && (
        <section className="mt-9" data-testid="account-notifications">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Notifications</h2>
          <div className="mt-3 space-y-2">
            {notifications.slice(0, 8).map((n) => (
              <div
                key={n.id}
                className="flex items-start gap-3 border border-stone-200 bg-white p-3"
                data-testid={`account-notification-${n.id}`}
              >
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-orange-50 text-orange-700">
                  <Bell className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-stone-900">{n.message}</p>
                  <p className="mt-0.5 text-xs text-stone-400">{new Date(n.at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-9">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Quick Door Now</h2>
        <div className="mt-3 border-y border-stone-200 bg-white px-4">
          <AccountLink
            icon={Package}
            label="My Orders"
            detail="Track current and past orders"
            to="/orders"
            testId="account-my-orders"
          />
          <AccountLink
            icon={Heart}
            label="Wishlist"
            detail="Products you saved for later"
            to="/wishlist"
            testId="account-wishlist"
          />
          <a
            href={`https://wa.me/91${(businessInfo.phone || "").replace(/\D/g, "")}?text=Hi%20Quick%20Door%20Now%2C%20I%20need%20help.`}
            target="_blank"
            rel="noreferrer"
            className="flex min-h-16 items-center gap-4 px-1 py-3 transition-colors hover:text-orange-700"
            data-testid="account-support"
          >
            <span className="grid h-10 w-10 place-items-center bg-stone-100 text-stone-900">
              <HelpCircle className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="flex-1 text-sm font-semibold text-stone-950">Help & Support</span>
            <ChevronRight className="h-4 w-4 text-stone-400" />
          </a>
        </div>
      </section>

      <section className="mt-9">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">Help & Support</h2>
        <div className="mt-3 rounded-xl border border-stone-200 bg-white px-4">
          <a
            href={`https://wa.me/91${(businessInfo.phone || "7980016662").replace(/\D/g, "")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="account-live-chat"
            className="flex min-h-14 items-center gap-3 border-b border-stone-100 text-sm font-semibold"
          >
            <MessageCircle size={18} className="text-emerald-600" />
            Live Chat
            <ChevronRight className="ml-auto h-4 w-4" />
          </a>
          {["Help & Support", "About Us", "Terms & Conditions", "Privacy Policy", "Refund Policy", "Cancellation Policy", "Shipping Policy"].map((label) => <Link key={label} to="/" data-testid={`account-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex min-h-12 items-center border-b border-stone-100 text-sm font-medium last:border-0">{label}<ChevronRight className="ml-auto h-4 w-4 text-stone-400" /></Link>)}
        </div>
      </section>

      {user?.role === "admin" && <Link to="/admin" data-testid="account-admin-dashboard" className="mt-6 flex min-h-14 items-center gap-3 rounded-xl bg-slate-950 px-4 text-sm font-bold text-white"><ShieldCheck size={20} className="text-orange-400" />Admin Dashboard<ChevronRight className="ml-auto" /></Link>}

      <section className="mt-9">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
              Saved addresses
            </h2>
            <p className="mt-1 text-sm text-stone-600">Choose a saved address faster at checkout.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowForm((value) => !value)}
            className={
              "inline-flex min-h-10 items-center gap-2 border border-stone-300 bg-white px-3 " +
              "text-xs font-semibold text-stone-900 transition-colors hover:border-orange-300 " +
              "hover:text-orange-700"
            }
            data-testid="add-address-button"
          >
            <Plus className="h-4 w-4" />
            Add address
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={saveAddress}
            className="mt-4 border border-stone-200 bg-white p-4"
            data-testid="saved-address-form"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-semibold text-stone-700">
                Label
                <input
                  required
                  value={address.label}
                  onChange={(event) => setAddress({ ...address, label: event.target.value })}
                  className="mt-1.5 min-h-11 w-full border border-stone-200 px-3 text-sm outline-none focus:border-orange-500"
                  data-testid="address-label-input"
                />
              </label>
              <label className="text-xs font-semibold text-stone-700">
                Mobile number
                <input
                  required
                  value={address.phone}
                  onChange={(event) => setAddress({ ...address, phone: event.target.value })}
                  className="mt-1.5 min-h-11 w-full border border-stone-200 px-3 text-sm outline-none focus:border-orange-500"
                  data-testid="address-phone-input"
                />
              </label>
            </div>
            <label className="mt-3 block text-xs font-semibold text-stone-700">
              Pin your location
              <div className="mt-1.5">
                <MapPicker
                  value={address.latitude && address.longitude ? { lat: address.latitude, lng: address.longitude } : null}
                  onChange={({ lat, lng, address: addr }) =>
                    setAddress((current) => ({
                      ...current,
                      latitude: lat,
                      longitude: lng,
                      address: addr || current.address,
                    }))
                  }
                  height={220}
                />
              </div>
            </label>
            <label className="mt-3 block text-xs font-semibold text-stone-700">
              Complete delivery address
              <textarea
                required
                rows={3}
                value={address.address}
                onChange={(event) => setAddress({ ...address, address: event.target.value })}
                className="mt-1.5 w-full border border-stone-200 px-3 py-2 text-sm outline-none focus:border-orange-500"
                data-testid="address-details-input"
              />
            </label>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                disabled={savingAddress}
                className="min-h-11 bg-orange-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-orange-700 disabled:opacity-60"
                data-testid="save-address-button"
              >
                {savingAddress ? "Saving…" : "Save address"}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="min-h-11 border border-stone-300 px-4 text-sm font-semibold text-stone-700"
                data-testid="cancel-address-button"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 space-y-3">
          {loadingAddresses && (
            <div className="h-24 animate-pulse border border-stone-200 bg-stone-100" data-testid="addresses-loading" />
          )}
          {!loadingAddresses && addresses.length === 0 && (
            <div className="border border-dashed border-stone-300 bg-white p-5" data-testid="addresses-empty">
              <HousePlus className="h-6 w-6 text-orange-600" />
              <p className="mt-3 text-sm font-semibold text-stone-950">No saved addresses</p>
              <p className="mt-1 text-sm text-stone-500">Add one now to speed up your next order.</p>
            </div>
          )}
          {addresses.map((savedAddress) => (
            <article
              key={savedAddress.id}
              className="flex items-start gap-3 border border-stone-200 bg-white p-4"
              data-testid={`saved-address-${savedAddress.id}`}
            >
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-stone-950">{savedAddress.label}</p>
                <p className="mt-1 text-sm leading-5 text-stone-600">{savedAddress.address}</p>
                <p className="mt-2 text-xs text-stone-500">{savedAddress.phone}</p>
              </div>
              <button
                type="button"
                aria-label={`Remove ${savedAddress.label} address`}
                onClick={() => removeAddress(savedAddress.id)}
                className="grid h-10 w-10 place-items-center text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
                data-testid={`delete-address-${savedAddress.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-9 border-t border-stone-200 pt-5">
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-red-50 text-sm font-bold text-red-700 transition-colors hover:bg-red-100"
          data-testid="account-logout"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </section>
    </main>
  );
}